import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import API from "../../services/api";
import { Toast } from "../../shared/Toast";

// Hook to fetch categories and expose a ready-to-use single-select
function useCategories({ defaultSelected = null } = {}) {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(defaultSelected);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const res = await API({
          url: "/plansummary/categories",
          method: "POST",
          responseType: "json",
        });

        if (res.code === 200 && isMounted) {
          setCategories(res.data || []);
        } else if (res?.message) {
          Toast("error", res.message);
        }
      } catch (error) {
        Toast("error", "Failed to load categories");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  const options = useMemo(
    () =>
      (categories || []).map((item) => ({
        value: item.category_id ?? item.value,
        label: item.category_name ?? item.label,
      })),
    [categories]
  );

  const normalizedSelected = useMemo(() => {
    if (!selectedCategory) return null;
    const value = selectedCategory.value ?? selectedCategory.category_id;
    return options.find((opt) => opt.value === value) || null;
  }, [options, selectedCategory]);

  const handleChange = useCallback((value) => {
    setSelectedCategory(value || null);
  }, []);

  const CategorySelect = useCallback(
    (props) => (
    <div className="!w-[200px] flex flex-col">
    <label className="form-label font-bold">Category</label>
      <Select
        isClearable
        isLoading={isLoading}
        options={options}
        value={normalizedSelected}
        onChange={handleChange}
        placeholder="Select category"
        classNamePrefix="category-select"
        isMulti={false}
        {...props}
      />
      </div>
    ),
    [handleChange, isLoading, normalizedSelected, options]
  );

  return {
    options, // categories list formatted for react-select
    selectedCategories: normalizedSelected,
    setSelectedCategory,
    CategorySelect,
    isLoading,
  };
}

export default useCategories;
